import React from 'react';

import { DownloadOutlined } from '@ant-design/icons';

import { WithId } from '@common/typescript/objects/WithId';
import { Pagination } from '@common/react/smart components/Table/TableDataProvider';
import { Nullable } from '@common/typescript/objects/Nullable';

import {
	DeleteRemote, DeleteWithRedux, EditButton, RequestButton, PrintButton,
} from '@app/components/UI/Buttons/ActionButtons';
import { ResourceButton } from '@app/components/UI/Buttons/ResourceButton';
import { PetMemorialEmailButton } from '@app/components/UI/Buttons/PetMemorialEmailButton';

export interface IActions {
	key: string;
	label: Nullable<React.ReactElement>;
}

export interface ActionFactoryParams<T extends WithId> {
	reload?: (offset?: number) => void;
	pagination?: Pagination;
	record: T;
	openDialog?: () => void;
	onCompleted?: () => void;
}

export type ActionFactory<T extends WithId> = (params: ActionFactoryParams<T>) => Array<IActions>;

interface EditConfig {
	openDialog?: () => void;
	linkPath?: string;
}

interface DeleteConfig<T extends WithId> {
	endpoint: string,
	itemName: string,
	reload?: () => void,
	record: T,
	pagination?: Pagination,
	onCompleted?: () => void,
	requestParams?: Record <string, unknown>,
	errorSettings?: (error: string) => void,
}

interface RequestConfig {
	endpoint: string,
	itemName: string,
	reload: () => void,
	disabled: boolean,
	requestParams?: Record <string, unknown>,
	message?: string,
	icon?: React.ReactNode,
}

interface PrintPdfConfig {
	link: string;
	text?: string;
}

export class ActionBuilder {
	private template: Array<IActions> = Array<IActions>();

	public build(): Array<IActions> {
		const result = this.template;
		this.template = Array<IActions>();

		return result;
	}

	public addEdit(config: EditConfig): ActionBuilder {
		this.template.push({
			key: 'edit',
			label: (
				<EditButton
					onClick={config.openDialog}
					linkPath={config.linkPath}
				/>
			),
		});

		return this;
	}

	public addDelete<T extends WithId>(config: DeleteConfig<T>): ActionBuilder {
		this.template.push({
			key: 'delete',
			label: (
				<DeleteRemote
					requestParams={config.requestParams}
					itemName={config.itemName}
					reload={config.reload}
					onCompleted={config.onCompleted}
					id={config.record.id}
					deleteEndpoint={config.endpoint}
					pagination={config.pagination}
					errorSettings={config.errorSettings}
				/>
			),
		});

		return this;
	}

	public addPetEmailButton<T extends WithId & { ownerEmail: string}>(
		record: T,
		display: boolean = true,
		allowResend: boolean = true,
		minified: boolean = true,
	): ActionBuilder {
		if (display) {
			this.template.push({
				key: 'email',
				label: (
					<PetMemorialEmailButton
						petId={record.id}
						email={record.ownerEmail}
						allowResend={allowResend}
						minified={minified}
					/>
				),
			});
		}

		return this;
	}

	public addPrintPdf(config: PrintPdfConfig): ActionBuilder {
		this.template.push({
			key: 'print',
			label: (
				<ResourceButton
					request={config.link}
					icon={<DownloadOutlined />}
					type="text"
					size="small"
				>
					{config.text ?? 'Print'}
				</ResourceButton>
			),
		});

		return this;
	}

	public addPrint<T extends WithId>(record: T): ActionBuilder {
		this.template.push({
			key: 'print',
			label: (<PrintButton id={record.id} />),
		});

		return this;
	}

	public addDeleteWithRedux<T extends WithId>(
		record: T,
		deleteItem: (e?: React.MouseEvent<HTMLElement, MouseEvent>) => void,
	): ActionBuilder {
		this.template.push({
			key: 'deleteWithRedux',
			label: (
				<DeleteWithRedux record={record} deleteItem={deleteItem} />
			),
		});

		return this;
	}

	public addRequestBtn(config: RequestConfig): ActionBuilder {
		this.template.push({
			key: 'request',
			label: (
				<RequestButton
					requestParams={config.requestParams}
					itemName={config.itemName}
					reload={config.reload}
					disabled={config.disabled}
					updateEndpoint={config.endpoint}
					message={config.message}
					icon={config.icon}
				/>
			),
		});

		return this;
	}

	public addBtn(
		button: React.ReactElement | null,
		key: string,
	): ActionBuilder {
		this.template.push({
			key,
			label: button,
		});

		return this;
	}
}
